import { useCallback, useEffect, useMemo, useState } from 'react'
import { GitMerge, Plus, Tags, Trash2, Zap } from 'lucide-react'
import { PageHeader } from '@/shell/AppShell'
import { EmptyState, ErrorState, Skeleton } from '@/components/States'
import { ApiError } from '@/lib/api'
import { useToast } from '@/providers/UiProviders'
import {
  fetchAllTickets,
  replyTicket,
  setTicketStatus,
  type AdminTicket,
} from '@/lib/adapters'
import { fetchCanned, type CannedResponse } from '../canned/cannedApi'
import {
  fetchTicketTags,
  createTicketTag,
  deleteTicketTag,
  mergeTickets,
  type TicketTag,
  TK_STR as S,
} from './ticketsApi'

/** A7.11 — Teg va kategoriyalar. Kategoriyalar JONLI (tiketlardan), teglar greenfield. */
export function TagsCategories() {
  const { toast } = useToast()
  const [cats, setCats] = useState<{ key: string; count: number }[] | null>(null)
  const [tags, setTags] = useState<TicketTag[]>([])
  const [name, setName] = useState('')
  const [busy, setBusy] = useState(false)
  const [tick, setTick] = useState(0)
  const reload = useCallback(() => setTick((t) => t + 1), [])

  useEffect(() => {
    const ac = new AbortController()
    fetchAllTickets(ac.signal)
      .then((rows) => {
        const m: Record<string, number> = {}
        for (const t of rows) m[t.category || '—'] = (m[t.category || '—'] ?? 0) + 1
        setCats(Object.entries(m).map(([key, count]) => ({ key, count })).sort((a, b) => b.count - a.count))
      })
      .catch(() => setCats([]))
    fetchTicketTags(ac.signal).then(setTags)
    return () => ac.abort()
  }, [tick])

  const add = async () => {
    if (!name.trim()) return
    setBusy(true)
    try {
      await createTicketTag(name.trim())
      setName('')
      toast(S.tagCreated, { tone: 'success' })
      reload()
    } catch {
      toast(S.failed, { tone: 'error' })
    } finally {
      setBusy(false)
    }
  }
  const del = async (id: number) => {
    try {
      await deleteTicketTag(id)
      reload()
    } catch {
      toast(S.failed, { tone: 'error' })
    }
  }

  return (
    <>
      <PageHeader title={S.tags} screenId="A7.11" description={S.tagsNote} />
      <div className="min-h-0 flex-1 overflow-auto p-3">
        <div className="mx-auto grid max-w-4xl gap-3 lg:grid-cols-2">
          <div className="rounded-lg border border-border-default bg-surface-2 p-3">
            <div className="mb-2 text-sm font-medium text-secondary">{S.liveCategories}</div>
            {!cats ? (
              <div className="space-y-1.5">{Array.from({ length: 5 }).map((_, i) => <Skeleton key={i} className="h-6 w-full" />)}</div>
            ) : cats.length === 0 ? (
              <div className="py-4 text-center text-sm text-tertiary">{S.empty}</div>
            ) : (
              <div className="space-y-1">
                {cats.map((c) => (
                  <div key={c.key} className="flex items-center justify-between rounded-md px-2 py-1 text-sm hover:bg-surface-3">
                    <span className="text-primary">{c.key}</span>
                    <span className="font-mono text-xs tabular text-tertiary">{c.count}</span>
                  </div>
                ))}
              </div>
            )}
          </div>

          <div className="rounded-lg border border-border-default bg-surface-2 p-3">
            <div className="mb-2 text-sm font-medium text-secondary">{S.manageTags}</div>
            <div className="mb-2 flex gap-2">
              <input
                value={name}
                onChange={(e) => setName(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && add()}
                placeholder={S.tagName}
                className="flex-1 rounded-md border border-border-default bg-surface-1 px-3 py-1.5 text-sm text-primary outline-none focus:border-accent"
              />
              <button onClick={add} disabled={busy || !name.trim()} className="flex items-center gap-1 rounded-md bg-accent px-3 py-1.5 text-sm text-white disabled:opacity-50">
                <Plus size={14} />
                {S.add}
              </button>
            </div>
            {tags.length === 0 ? (
              <div className="py-4 text-center text-sm text-tertiary">{S.greenfield}</div>
            ) : (
              <div className="flex flex-wrap gap-1.5">
                {tags.map((t) => (
                  <span key={t.id} className="inline-flex items-center gap-1.5 rounded-full border border-border-default bg-surface-3 px-2.5 py-1 text-xs text-secondary">
                    {t.name}
                    {t.ticket_count > 0 && <span className="text-tertiary">· {t.ticket_count}</span>}
                    <button onClick={() => del(t.id)} className="text-tertiary hover:text-danger"><Trash2 size={12} /></button>
                  </span>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    </>
  )
}

const norm = (s: string) => s.toLowerCase().replace(/\s+/g, ' ').trim()

/** A7.12 — Birlashtirish (duplikatlar). O'xshash tiketlar (bir user + bir mavzu) → asosiyga birlashtirish. */
export function MergeDuplicates() {
  const { toast } = useToast()
  const [rows, setRows] = useState<AdminTicket[] | null>(null)
  const [err, setErr] = useState<ApiError | null>(null)
  const [tick, setTick] = useState(0)
  const [busy, setBusy] = useState(false)
  const reload = useCallback(() => setTick((t) => t + 1), [])

  useEffect(() => {
    const ac = new AbortController()
    setErr(null)
    fetchAllTickets(ac.signal)
      .then(setRows)
      .catch((e) => {
        if (!ac.signal.aborted) setErr(e instanceof ApiError ? e : new ApiError('INTERNAL', String(e), 500))
      })
    return () => ac.abort()
  }, [tick])

  const groups = useMemo(() => {
    const map = new Map<string, AdminTicket[]>()
    for (const t of rows ?? []) {
      if (!t.subject) continue
      const k = `${t.user_id}::${norm(t.subject)}`
      map.set(k, [...(map.get(k) ?? []), t])
    }
    return [...map.values()]
      .filter((g) => g.length > 1)
      .map((g) => {
        const all = [...g].sort((a, b) => a.id - b.id)
        return { primary: all[0]!, dupes: all.slice(1), all }
      })
  }, [rows])

  const merge = async (primary: AdminTicket, dupes: AdminTicket[]) => {
    setBusy(true)
    try {
      await Promise.all(dupes.map((d) => mergeTickets(d.id, primary.id)))
      toast(S.mergeDone, { tone: 'success' })
      reload()
    } catch {
      toast(S.failed, { tone: 'error' })
    } finally {
      setBusy(false)
    }
  }

  return (
    <>
      <PageHeader title={S.merge} screenId="A7.12" description={S.mergeNote} />
      <div className="min-h-0 flex-1 overflow-auto p-3">
        <div className="mx-auto max-w-3xl space-y-3">
          {err ? (
            <ErrorState code={err.code} onRetry={reload} />
          ) : !rows ? (
            <div className="space-y-1.5">{Array.from({ length: 4 }).map((_, i) => <Skeleton key={i} className="h-24 w-full" />)}</div>
          ) : groups.length === 0 ? (
            <EmptyState title={S.noDuplicates} icon={<GitMerge size={28} strokeWidth={1.5} />} />
          ) : (
            groups.map(({ primary, dupes, all }) => (
              <div key={primary.id} className="rounded-lg border border-border-default bg-surface-2 p-3">
                <div className="mb-2 flex items-center justify-between">
                  <span className="text-sm font-medium text-primary">{primary.subject}</span>
                  <button
                    disabled={busy}
                    onClick={() => merge(primary, dupes)}
                    className="inline-flex items-center gap-1 rounded-md border border-accent bg-accent-subtle px-2.5 py-1 text-xs text-accent hover:bg-accent hover:text-white disabled:opacity-50"
                  >
                    <GitMerge size={13} />
                    {S.mergeInto} #{primary.id}
                  </button>
                </div>
                <div className="space-y-1">
                  {all.map((t) => (
                    <div key={t.id} className="flex items-center gap-2 text-xs">
                      <span className={`rounded px-1.5 py-0.5 ${t.id === primary.id ? 'bg-accent-subtle text-accent' : 'bg-surface-3 text-tertiary'}`}>
                        {t.id === primary.id ? S.primary : `#${t.id}`}
                      </span>
                      <span className="text-secondary">{t.status}</span>
                      <span className="text-tertiary">{t.category}</span>
                    </div>
                  ))}
                </div>
              </div>
            ))
          )}
        </div>
      </div>
    </>
  )
}

const MACRO_STATUSES = ['open', 'pending', 'closed'] as const

/** A7.16 — Makroslar. Javob shabloni (canned, JONLI) + holat o'zgarishi bir klikda (JONLI reply+status). */
export function MacrosScreen() {
  const { toast } = useToast()
  const [templates, setTemplates] = useState<CannedResponse[] | null>(null)
  const [tplId, setTplId] = useState('')
  const [ticketId, setTicketId] = useState('')
  const [thenStatus, setThenStatus] = useState('')
  const [busy, setBusy] = useState(false)

  useEffect(() => {
    const ac = new AbortController()
    fetchCanned('', ac.signal).then(setTemplates).catch(() => setTemplates([]))
    return () => ac.abort()
  }, [])

  const tpl = templates?.find((t) => String(t.id) === tplId) ?? null
  const tid = Number(ticketId)
  const canRun = tid > 0 && (tpl != null || thenStatus !== '')

  const run = async () => {
    if (!canRun) return
    setBusy(true)
    try {
      if (tpl) await replyTicket(tid, tpl.body)
      if (thenStatus) await setTicketStatus(tid, thenStatus)
      toast(S.macroDone, { tone: 'success' })
      setTicketId('')
    } catch {
      toast(S.failed, { tone: 'error' })
    } finally {
      setBusy(false)
    }
  }

  return (
    <>
      <PageHeader title={S.macros} screenId="A7.16" description={S.macrosNote} />
      <div className="min-h-0 flex-1 overflow-auto p-3">
        <div className="mx-auto max-w-2xl space-y-3">
          <div className="space-y-2.5 rounded-lg border border-border-default bg-surface-2 p-3">
            <label className="block">
              <span className="mb-1 block text-xs text-tertiary">{S.template}</span>
              <select value={tplId} onChange={(e) => setTplId(e.target.value)} className="w-full rounded-md border border-border-default bg-surface-1 px-2.5 py-2 text-sm text-primary">
                <option value="">{S.pickTemplate}</option>
                {(templates ?? []).map((t) => (
                  <option key={t.id} value={t.id}>{t.title} · {t.category}</option>
                ))}
              </select>
            </label>
            {tpl && <p className="whitespace-pre-wrap rounded-md bg-surface-1 p-2 text-xs text-secondary">{tpl.body}</p>}
            <div className="flex gap-2">
              <label className="flex-1">
                <span className="mb-1 block text-xs text-tertiary">{S.targetTicket}</span>
                <input
                  value={ticketId}
                  inputMode="numeric"
                  onChange={(e) => setTicketId(e.target.value.replace(/\D/g, ''))}
                  placeholder="#"
                  className="w-full rounded-md border border-border-default bg-surface-1 px-2.5 py-2 text-sm text-primary outline-none focus:border-accent"
                />
              </label>
              <label className="flex-1">
                <span className="mb-1 block text-xs text-tertiary">{S.thenStatus}</span>
                <select value={thenStatus} onChange={(e) => setThenStatus(e.target.value)} className="w-full rounded-md border border-border-default bg-surface-1 px-2.5 py-2 text-sm text-primary">
                  <option value="">—</option>
                  {MACRO_STATUSES.map((st) => (
                    <option key={st} value={st}>{st}</option>
                  ))}
                </select>
              </label>
            </div>
            <button
              disabled={busy || !canRun}
              onClick={run}
              className="inline-flex items-center gap-1.5 rounded-md bg-accent px-4 py-2 text-sm text-white disabled:opacity-50"
            >
              <Zap size={15} />
              {S.runMacro}
            </button>
          </div>

          {templates != null && templates.length === 0 && (
            <div className="flex items-center gap-2 rounded-lg border border-border-default bg-surface-2 p-3 text-sm text-tertiary">
              <Tags size={16} />
              {S.noTemplates}
            </div>
          )}
        </div>
      </div>
    </>
  )
}
